<template>
  <v-container
    fluid
    class="platform-page responsive-height"
    :class="$vuetify.theme.dark ? 'platform-page--dark' : 'platform-page--light'"
  >
    <section class="platform-hero">
      <div class="platform-hero__content">
        <div class="platform-hero__icon" aria-hidden="true">
          <v-icon>mdi-tray-arrow-down</v-icon>
        </div>

        <div class="platform-hero__copy">
          <h1>Download Trading Platforms</h1>
          <p>Choose your operating system and download our secure trading platform.</p>
        </div>
      </div>

      <div class="platform-hero__art" aria-hidden="true">
        <svg
          class="platform-hero__waves"
          viewBox="0 0 760 150"
          preserveAspectRatio="none"
        >
          <path
            v-for="line in 10"
            :key="line"
            :d="wavePath(line)"
            fill="none"
            stroke="currentColor"
            :stroke-opacity="0.18 - (line * 0.011)"
            stroke-width="1"
          />
        </svg>

        <svg class="platform-hero__monitor" viewBox="0 0 164 126">
          <path
            d="M24 8h124a8 8 0 0 1 8 9l-8 78a8 8 0 0 1-8 7H15a8 8 0 0 1-8-9l9-78a8 8 0 0 1 8-7Z"
            fill="currentColor"
            opacity=".12"
          />
          <path
            d="M29 14h113a6 6 0 0 1 6 7l-7 66a6 6 0 0 1-6 5H22a6 6 0 0 1-6-7l7-66a6 6 0 0 1 6-5Z"
            fill="currentColor"
            opacity=".08"
            stroke="currentColor"
            stroke-width="2"
          />
          <path d="M75 93h22l3 15H70l5-15Z" fill="currentColor" opacity=".15" />
          <path d="M60 108h50" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="5" opacity=".18" />
          <path d="M34 67l19-25 18 17 21-28 16 15 25-24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" />
          <path d="m123 22 10 0-1 10" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" />
          <path d="M34 76h91" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="3" opacity=".18" />
          <rect x="32" y="25" width="17" height="5" rx="2.5" fill="currentColor" opacity=".28" />
        </svg>

        <div class="platform-hero__download">
          <v-icon>mdi-tray-arrow-down</v-icon>
        </div>
      </div>
    </section>

    <section class="platform-grid" aria-label="Trading platform downloads">
      <article
        v-for="platform in platformsList"
        :key="platform.type"
        class="platform-download-card"
        :class="`platform-download-card--${platform.type}`"
      >
        <span class="platform-download-card__badge">{{ platform.badge }}</span>

        <div class="platform-download-card__body">
          <div class="platform-download-card__icon" aria-hidden="true">
            <img :src="platform.icon" :alt="`${platform.badge} logo`" />
          </div>

          <h2>{{ platform.title }}</h2>
          <p class="platform-download-card__server">
            Server:
            <span data-dynamic-content>{{ platform.server_name }}</span>
          </p>

          <ul class="platform-download-card__features">
            <li v-for="feature in platform.features" :key="feature">
              <v-icon>mdi-check-circle-outline</v-icon>
              <span>{{ feature }}</span>
            </li>
          </ul>
        </div>

        <button
          type="button"
          class="platform-download-card__button"
          :aria-label="`Download ${platform.title}`"
          @click="openExternalLink(platform.download_link)"
        >
          <v-icon>mdi-tray-arrow-down</v-icon>
          <span>FREE DOWNLOAD</span>
        </button>
      </article>
    </section>

    <section class="platform-security">
      <div class="platform-security__content">
        <div class="platform-security__icon" aria-hidden="true">
          <v-icon>mdi-shield-check-outline</v-icon>
        </div>

        <div>
          <h2>Secure &amp; Trusted</h2>
          <p>All our platforms are encrypted and secure. Your funds and data are always protected.</p>
        </div>
      </div>

      <a href="#platform-security" @click.prevent>
        <span>Learn more about security</span>
        <v-icon>mdi-chevron-right</v-icon>
      </a>
    </section>
  </v-container>
</template>

<script>
import { PLATFORMS__ACTIONS } from "@/store/action-types";
import { mapActions, mapState } from "vuex";

export default {
  name: "PlatformView",

  computed: {
    ...mapState("platform", ["platforms"]),

    platformsList() {
      const platform = this.platforms[0] || {};
      const serverName = platform.server_name || "UnitedInternationalTrading-Real";

      return [
        {
          type: "android",
          badge: "Android",
          title: "Android Platform",
          icon: require("../../assets/android.png"),
          server_name: serverName,
          download_link: platform.android_download_link,
          features: [
            "Full trading functionality",
            "Real-time market data",
            "Secure and fast execution",
          ],
        },
        {
          type: "windows",
          badge: "Windows",
          title: "Windows Platform",
          icon: require("../../assets/windows.png"),
          server_name: serverName,
          download_link: platform.windows_download_link,
          features: [
            "Advanced charting tools",
            "Multiple order types",
            "Expert Advisors support",
          ],
        },
        {
          type: "macos",
          badge: "macOS",
          title: "macOS Platform",
          icon: require("../../assets/apple.png"),
          server_name: serverName,
          download_link: platform.ios_download_link,
          features: [
            "Optimized for Mac",
            "High performance",
            "Secure and reliable",
          ],
        },
      ];
    },
  },

  methods: {
    ...mapActions("platform", [PLATFORMS__ACTIONS.DOWNLOADPLATFORMS]),

    openExternalLink(url) {
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },

    wavePath(line) {
      const offset = line * 4;
      return `M0 ${107 + offset} C105 ${20 + offset}, 170 ${20 + offset}, 275 ${107 + offset} S445 ${194 + offset}, 550 ${107 + offset} S655 ${20 + offset}, 760 ${107 + offset}`;
    },
  },

  async mounted() {
    document.body.classList.add("platform-page-active");

    try {
      await this[PLATFORMS__ACTIONS.DOWNLOADPLATFORMS]();
    } catch (error) {
      console.error("Unable to load platform downloads:", error);
    }
  },

  beforeDestroy() {
    document.body.classList.remove("platform-page-active");
  },
};
</script>

<style scoped>
.platform-page {
  --platform-blue: #d99a00;
  --platform-blue-rgb: 217, 154, 0;
  --platform-green: #d99a00;
  --platform-green-rgb: 217, 154, 0;
  --platform-mac: #d99a00;
  --platform-mac-rgb: 217, 154, 0;
  --page-background: #f7f8fa;
  --surface-background: #ffffff;
  --surface-border: #dfe2e6;
  --heading-color: #171a1f;
  --body-color: #30343a;
  --muted-color: #626973;
  --icon-orbit: rgba(255, 247, 223, 0.92);
  --hero-icon-background: linear-gradient(145deg, #ffd43b 0%, #ffc400 100%);
  --hero-icon-color: #17130a;
  --shadow-color: rgba(42, 34, 9, 0.04);

  max-width: none !important;
  padding: 12px 20px 16px;
  background: var(--page-background);
  color: var(--body-color);
}

.platform-page--dark {
  --page-background: #030b13;
  --surface-background: linear-gradient(145deg, rgba(7, 17, 27, 0.99), rgba(4, 13, 21, 0.99));
  --surface-border: #293026;
  --heading-color: #f4f5f7;
  --body-color: #d9dde2;
  --muted-color: #aeb6c0;
  --icon-orbit: rgba(255, 196, 0, 0.075);
  --hero-icon-background: linear-gradient(145deg, rgba(255, 212, 59, 0.98), rgba(217, 154, 0, 0.96));
  --hero-icon-color: #17130a;
  --shadow-color: rgba(0, 0, 0, 0.22);
  --platform-blue: #ffc400;
  --platform-blue-rgb: 255, 196, 0;
  --platform-green: #ffc400;
  --platform-green-rgb: 255, 196, 0;
  --platform-mac: #ffc400;
  --platform-mac-rgb: 255, 196, 0;
}

.platform-hero,
.platform-download-card,
.platform-security {
  border: 1px solid var(--surface-border);
  background: var(--surface-background);
  box-shadow: 0 10px 30px var(--shadow-color);
}

.platform-hero {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 100px;
  padding: 15px 22px;
  border-radius: 16px;
  overflow: hidden;
}

.platform-hero__content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 20px;
}

.platform-hero__icon {
  display: flex;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border: 1px solid rgba(217, 154, 0, 0.72);
  border-radius: 12px;
  background: var(--hero-icon-background);
  box-shadow: 0 12px 28px rgba(217, 154, 0, 0.18);
}

.platform-hero__icon .v-icon {
  color: var(--hero-icon-color);
  font-size: 29px;
}

.platform-hero__copy h1 {
  margin: 0 0 6px;
  color: var(--heading-color);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.35px;
}

.platform-hero__copy p {
  margin: 0;
  color: var(--body-color);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.55;
}

.platform-hero__art {
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  width: 57%;
  height: 100%;
  color: var(--platform-blue);
}

.platform-hero__waves {
  position: absolute;
  right: 48px;
  bottom: -19px;
  width: 92%;
  height: 122px;
}

.platform-hero__monitor {
  position: absolute;
  top: 12px;
  right: 20px;
  width: 112px;
  height: 86px;
  transform: rotate(4deg);
}

.platform-hero__download {
  position: absolute;
  right: 27px;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #ffc400;
  box-shadow: 0 8px 18px rgba(217, 154, 0, 0.28);
}

.platform-hero__download .v-icon {
  color: #17130a;
  font-size: 23px;
}

.platform-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
}

.platform-download-card {
  --card-accent: var(--platform-blue);
  --card-accent-rgb: var(--platform-blue-rgb);

  position: relative;
  display: flex;
  min-width: 0;
  min-height: 360px;
  padding: 18px 22px;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
}

.platform-download-card--android {
  --card-accent: var(--platform-green);
  --card-accent-rgb: var(--platform-green-rgb);
}

.platform-download-card--macos {
  --card-accent: var(--platform-mac);
  --card-accent-rgb: var(--platform-mac-rgb);
}

.platform-download-card__badge {
  position: absolute;
  top: 19px;
  right: 20px;
  z-index: 2;
  padding: 6px 10px;
  border: 1px solid rgba(var(--card-accent-rgb), 0.12);
  border-radius: 8px;
  background: rgba(var(--card-accent-rgb), 0.11);
  color: var(--card-accent);
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
}

.platform-page--dark .platform-download-card__badge {
  background: rgba(var(--card-accent-rgb), 0.18);
}

.platform-download-card__body {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

.platform-download-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
  margin: 10px auto 10px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.5), transparent 48%),
    rgba(var(--card-accent-rgb), 0.09);
}

.platform-page--dark .platform-download-card__icon {
  background:
    radial-gradient(circle at 35% 30%, rgba(var(--card-accent-rgb), 0.12), transparent 56%),
    var(--icon-orbit);
}

.platform-download-card__icon img {
  display: block;
  width: 54px;
  height: 54px;
  object-fit: contain;
}

.platform-download-card--android .platform-download-card__icon img {
  filter: brightness(0.83) saturate(1.45) hue-rotate(-18deg);
}

.platform-page--dark .platform-download-card--android .platform-download-card__icon img {
  filter: none;
}

.platform-download-card--windows .platform-download-card__icon img {
  filter: saturate(1.5) hue-rotate(18deg);
}

.platform-download-card--macos .platform-download-card__icon img {
  width: 52px;
  height: 52px;
  filter: brightness(0.5);
}

.platform-page--dark .platform-download-card--macos .platform-download-card__icon img {
  filter: brightness(1.16);
}

.platform-download-card__icon img {
  filter: brightness(0) saturate(100%) invert(74%) sepia(97%) saturate(1500%) hue-rotate(2deg) brightness(103%) contrast(103%) !important;
}

.platform-download-card h2 {
  margin: 0 0 8px;
  color: var(--heading-color);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  letter-spacing: -0.3px;
}

.platform-download-card__server {
  width: 100%;
  margin: 0;
  color: var(--body-color);
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  white-space: nowrap;
}

.platform-download-card__server span {
  color: var(--platform-blue);
  font-weight: 500;
}

.platform-download-card__features {
  width: 100%;
  max-width: 360px;
  margin: 14px auto 10px;
  padding: 0;
  list-style: none;
}

.platform-download-card__features li {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 24px;
  color: var(--body-color);
  font-size: 14px;
  line-height: 1.45;
}

.platform-download-card__features .v-icon {
  flex: 0 0 auto;
  color: var(--card-accent);
  font-size: 18px;
}

.platform-download-card__button {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 42px;
  padding: 0 18px;
  border: 1px solid var(--card-accent);
  border-radius: 9px;
  outline: none;
  background: transparent;
  color: var(--card-accent);
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  transition: background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.platform-download-card__button .v-icon {
  color: currentColor;
  font-size: 22px;
}

.platform-download-card__button:hover,
.platform-download-card__button:focus-visible {
  background: rgba(var(--card-accent-rgb), 0.08);
  box-shadow: 0 7px 18px rgba(var(--card-accent-rgb), 0.12);
  transform: translateY(-1px);
}

.platform-download-card__button:active {
  transform: translateY(0);
}

.platform-security {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 80px;
  margin-top: 14px;
  padding: 12px 20px;
  border-radius: 16px;
}

.platform-security__content {
  display: flex;
  align-items: center;
  gap: 18px;
}

.platform-security__icon {
  display: flex;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(var(--platform-blue-rgb), 0.08);
}

.platform-security__icon .v-icon {
  color: var(--platform-blue);
  font-size: 30px;
}

.platform-security h2 {
  margin: 0 0 3px;
  color: var(--heading-color);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
}

.platform-security p {
  margin: 0;
  color: var(--muted-color);
  font-size: 13px;
  line-height: 1.45;
}

.platform-security a {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--platform-blue);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}

.platform-security a .v-icon {
  color: inherit;
  font-size: 24px;
}

@media (min-width: 1600px) and (min-height: 900px) {
  .platform-page {
    padding-top: 16px;
  }

  .platform-hero {
    min-height: 106px;
  }

  .platform-download-card {
    min-height: 360px;
  }

  .platform-page--dark {
    padding-top: 16px;
  }

  .platform-page--dark .platform-hero {
    min-height: 100px;
    padding-right: 25px;
    padding-left: 25px;
  }

  .platform-page--dark .platform-hero__content {
    gap: 23px;
  }

  .platform-page--dark .platform-hero__icon {
    flex-basis: 64px;
    width: 64px;
    height: 64px;
  }

  .platform-page--dark .platform-grid {
    margin-top: 14px;
  }

  .platform-page--dark .platform-download-card__icon {
    width: 90px;
    height: 90px;
    margin-top: 10px;
  }

  .platform-page--dark .platform-download-card__features {
    max-width: 340px;
  }

  .platform-page--dark .platform-security {
    min-height: 80px;
    margin-top: 14px;
  }

  .platform-page--light .platform-security {
    min-height: 80px;
    margin-top: 14px;
  }
}

@media (max-width: 1260px) {
  .platform-page {
    padding-right: 18px;
    padding-left: 18px;
  }

  .platform-download-card {
    padding-right: 23px;
    padding-left: 23px;
  }

  .platform-download-card__server {
    font-size: 13px;
  }
}

@media (max-width: 1050px) {
  .platform-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .platform-download-card:last-child {
    grid-column: 1 / -1;
    width: calc(50% - 10px);
    justify-self: center;
  }
}

@media (max-width: 760px) {
  .platform-page {
    padding: 16px 14px 24px;
  }

  .platform-hero {
    min-height: 150px;
    padding: 24px;
  }

  .platform-hero__content {
    gap: 18px;
  }

  .platform-hero__icon {
    flex-basis: 62px;
    width: 62px;
    height: 62px;
  }

  .platform-hero__copy h1 {
    font-size: 20px;
  }

  .platform-hero__copy p {
    font-size: 13px;
  }

  .platform-hero__art {
    opacity: 0.28;
    width: 70%;
  }

  .platform-hero__monitor,
  .platform-hero__download {
    display: none;
  }

  .platform-grid {
    grid-template-columns: 1fr;
  }

  .platform-download-card,
  .platform-download-card:last-child {
    grid-column: auto;
    width: 100%;
  }

  .platform-security {
    align-items: flex-start;
    gap: 20px;
    flex-direction: column;
  }

  .platform-security a {
    align-self: flex-end;
  }
}

@media (max-width: 460px) {
  .platform-hero__icon {
    display: none;
  }

  .platform-download-card {
    min-height: 360px;
    padding: 27px 20px 22px;
  }

  .platform-download-card__server {
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .platform-security__content {
    align-items: flex-start;
    gap: 16px;
  }

  .platform-security__icon {
    flex-basis: 48px;
    width: 48px;
    height: 48px;
  }
}
</style>

<style>
body.platform-page-active .wrapper.theme--light,
body.platform-page-active .wrapper.theme--light .v-main,
body.platform-page-active .wrapper.theme--light .custom-navbar,
body.platform-page-active .wrapper.theme--light .custom-width-navigation-drawer,
body.platform-page-active .wrapper.theme--light .sidebar-navigation-drawer,
body.platform-page-active .wrapper.theme--light .sidebar-navigation-drawer .v-list {
  background-color: #ffffff !important;
}

body.platform-page-active .wrapper.theme--dark,
body.platform-page-active .wrapper.theme--dark .v-main,
body.platform-page-active .wrapper.theme--dark .custom-navbar,
body.platform-page-active .wrapper.theme--dark .custom-width-navigation-drawer,
body.platform-page-active .wrapper.theme--dark .sidebar-navigation-drawer,
body.platform-page-active .wrapper.theme--dark .sidebar-navigation-drawer .v-list {
  background-color: #030b13 !important;
}

body.platform-page-active .custom-navbar {
  border-bottom: 1px solid rgba(255, 196, 0, 0.12) !important;
}

body.platform-page-active .sidebar-navigation-drawer {
  border-right: 1px solid rgba(255, 196, 0, 0.14) !important;
}

body.platform-page-active .wrapper.theme--light .sidebar-container .text--ash,
body.platform-page-active .wrapper.theme--light .sidebar-container .v-icon {
  color: #111833 !important;
}

body.platform-page-active .wrapper.theme--dark .sidebar-container .text--ash,
body.platform-page-active .wrapper.theme--dark .sidebar-container .v-icon {
  color: #d9dde2 !important;
}

body.platform-page-active .sidebar-container .single-menu-item:has(.router-link-exact-active) {
  width: auto;
  margin: 0 12px;
  border-radius: 8px;
  background: linear-gradient(105deg, rgba(255, 196, 0, 0.18), rgba(217, 154, 0, 0.08));
}

body.platform-page-active .wrapper.theme--dark .sidebar-container .single-menu-item:has(.router-link-exact-active) {
  background: linear-gradient(105deg, rgba(255, 196, 0, 0.26), rgba(255, 196, 0, 0.07));
}

body.platform-page-active .sidebar-container .single-menu-item:has(.router-link-exact-active) .v-icon,
body.platform-page-active .sidebar-container .single-menu-item:has(.router-link-exact-active) a {
  color: #b47f00 !important;
}

body.platform-page-active .wrapper.theme--dark .sidebar-container .single-menu-item:has(.router-link-exact-active) .v-icon,
body.platform-page-active .wrapper.theme--dark .sidebar-container .single-menu-item:has(.router-link-exact-active) a {
  color: #ffc400 !important;
}

body.platform-page-active .custom-navbar .top-links-list .v-btn {
  border: 1px solid rgba(255, 196, 0, 0.18);
}

body.platform-page-active .custom-navbar .top-links-list .v-icon {
  color: #ffc400 !important;
}
</style>
